import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url, chatbotUrl } from '../../util/config';
import Step0 from './Step0';

// ── Context-aware examples per template ───────────────────────────────────────
const TEMPLATE_EXAMPLES = {
    emi_reminder: {
        objective:    'Remind customers about their upcoming EMI payment, confirm their intent to pay, and provide payment options if needed.',
        guidelines:   'Be polite and professional. Always address customer by name. Never threaten or use aggressive language. Follow RBI guidelines. Offer alternative payment dates if customer requests.',
        faqs: [
            { question: 'Can I postpone my EMI?', answer: 'You can request a 7-day extension by visiting your nearest branch or calling 1800-XXX-XXXX.' },
            { question: 'What happens if I miss EMI?', answer: 'A late payment fee may apply. Please contact us immediately to avoid any impact on your credit score.' },
        ],
        variables:  ['customer_name', 'mobile_number', 'emi_amount', 'due_date', 'loan_account'],
        guardrails: ['Never threaten legal action or use intimidating language', 'Never share account details without proper verification', 'Always offer option to speak with human agent', 'Follow RBI FLDG guidelines strictly'],
        handoff_keywords: ['legal', 'complaint', 'lawyer', 'court', 'rbi', 'ombudsman'],
    },
    loan_eligibility: {
        objective:    'Qualify home loan leads by collecting income details, property value, and employment information. Check eligibility criteria and schedule a callback for interested leads.',
        guidelines:   'Be conversational and encouraging. Explain each step clearly. Never commit to loan approval on behalf of the bank. Maintain customer data confidentiality at all times.',
        faqs: [
            { question: 'What is the minimum income required?', answer: 'Minimum monthly income of ₹25,000 for salaried and ₹3 lakh annual turnover for self-employed.' },
            { question: 'How long does approval take?', answer: 'Pre-approval takes 24 hours. Final approval depends on document verification, typically 7-10 working days.' },
        ],
        variables:  ['customer_name', 'mobile_number', 'monthly_income', 'property_value', 'employment_type'],
        guardrails: ['Never guarantee loan approval under any circumstances', 'Collect only minimum required information', 'Always inform customer their data will be verified by our team'],
        handoff_keywords: ['fraud', 'complaint', 'manager', 'legal', 'reject'],
    },
    account_support: {
        objective:    'Help customers with account queries including balance check, statement requests, debit card blocking, and dispute resolution. Escalate complex issues to human agents.',
        guidelines:   'Always verify customer identity before sharing any account information. Be precise and concise. Never share full account or card numbers. Escalate fraud cases immediately.',
        faqs: [
            { question: 'How do I block my debit card?', answer: 'You can block your card instantly via our mobile app, internet banking, or by calling 1800-XXX-XXXX.' },
            { question: 'How do I get my account statement?', answer: 'Statements are available on mobile app and internet banking for up to 2 years. You can also email us at support@bank.com.' },
        ],
        variables:  ['customer_name', 'account_number', 'mobile_number'],
        guardrails: ['Always verify customer identity before sharing account details', 'Never reveal full account or card numbers in chat', 'Escalate fraud and unauthorized transactions to human immediately'],
        handoff_keywords: ['fraud', 'unauthorized', 'stolen', 'complaint', 'legal', 'rbi'],
    },
    order_tracking: {
        objective:    'Help customers track their orders, get real-time delivery updates, and resolve delivery issues including delays, wrong items, and missing packages.',
        guidelines:   'Always check latest order status before responding. Be empathetic for delays. Proactively offer compensation for significant delays. Never promise specific delivery times without checking.',
        faqs: [
            { question: 'Where is my order?', answer: 'Share your order ID and I\'ll check the latest status for you right away.' },
            { question: 'My order is delayed. What do I do?', answer: 'I apologize for the delay. Let me check the status and I\'ll also process a compensation coupon for you.' },
        ],
        variables:  ['customer_name', 'order_id', 'expected_date', 'delivery_address'],
        guardrails: ['Never promise specific delivery times without checking with logistics', 'Always offer refund option for orders not delivered within 2x expected time'],
        handoff_keywords: ['fraud', 'wrong address', 'stolen', 'complaint', 'legal'],
    },
    appointment: {
        objective:    'Help patients book, reschedule, or cancel doctor appointments. Collect patient details, preferred timing, and appointment type. Confirm all details before ending conversation.',
        guidelines:   'Be gentle, patient, and professional. Respect patient privacy at all times. Always confirm appointment details clearly at end. Never provide medical advice.',
        faqs: [
            { question: 'Can I book for a family member?', answer: 'Yes, you can book for a family member. Please provide their name, age, and contact number.' },
            { question: 'What documents do I need?', answer: 'Please carry your previous prescription, insurance card (if applicable), and a valid photo ID.' },
        ],
        variables:  ['patient_name', 'mobile_number', 'doctor_name', 'preferred_date', 'appointment_type'],
        guardrails: ['Never provide medical advice or suggest diagnosis', 'Always recommend consulting doctor for health queries', 'Maintain strict patient data confidentiality'],
        handoff_keywords: ['emergency', 'urgent', 'critical', 'complaint', 'doctor'],
    },
    candidate: {
        objective:    'Screen job candidates for open positions by collecting experience, current CTC, expected CTC, notice period, and location preference. Shortlist based on defined criteria.',
        guidelines:   'Be professional, warm, and respectful. Explain the role and company culture clearly. Thank every candidate regardless of outcome. Never ask about personal details like marital status or age.',
        faqs: [
            { question: 'Is this a work from home position?', answer: 'This is a hybrid role — 3 days work from office, 2 days from home per week.' },
            { question: 'When will I hear back?', answer: 'Our team will review your profile within 2-3 working days and reach out if you are shortlisted.' },
        ],
        variables:  ['candidate_name', 'position', 'experience_years', 'current_ctc', 'expected_ctc', 'notice_period'],
        guardrails: ['Never ask about age, marital status, religion, or personal details', 'Always explain next steps clearly', 'Thank every candidate for their time'],
        handoff_keywords: ['complaint', 'discrimination', 'legal', 'hr manager'],
    },
};

// ── Default examples (when no template selected) ──────────────────────────────
const DEFAULT_EXAMPLES = {
    objective:    'Help customers with their queries, provide accurate information from our knowledge base, and escalate complex issues to human agents when needed.',
    guidelines:   'Be polite, professional, and empathetic. Respond concisely. Use customer\'s name when known. Escalate to human agent if query is unresolved after 3 attempts.',
    faqs: [
        { question: 'What are your working hours?', answer: 'We are available 24/7 via WhatsApp. For urgent matters, call 1800-XXX-XXXX.' },
        { question: 'How do I contact support?', answer: 'You can reach us here on WhatsApp, by email at support@company.com, or call us at 1800-XXX-XXXX.' },
    ],
    variables:  ['customer_name', 'mobile_number'],
    guardrails: ['Always be polite and professional', 'Never share sensitive customer information', 'Escalate unresolved issues to human agent'],
    handoff_keywords: ['complaint', 'manager', 'supervisor', 'legal', 'urgent'],
};

// ── Industry Templates ────────────────────────────────────────────────────────
const TEMPLATES = {
    banking: [
        { id: 'emi_reminder',    name: 'EMI Reminder',      icon: '💳', desc: 'Automated payment reminder before due date',    mode: 'simple',     ...TEMPLATE_EXAMPLES.emi_reminder },
        { id: 'loan_eligibility',name: 'Loan Eligibility',  icon: '🏠', desc: 'Qualify leads and collect financial details',   mode: 'structured', ...TEMPLATE_EXAMPLES.loan_eligibility },
        { id: 'credit_card',     name: 'Credit Card Lead',  icon: '💳', desc: 'Pre-approved card offer outreach',              mode: 'simple',
          objective: 'Inform customer about their pre-approved credit card offer, explain key benefits, and collect their interest confirmation.',
          guidelines: 'Be enthusiastic but not pushy. Clearly explain benefits. Never ask for OTP or full card details.',
          faqs: [{ question: 'Is there an annual fee?', answer: 'The first year annual fee is waived. From second year, ₹999 + GST applies.' }],
          variables: ['customer_name', 'mobile_number', 'credit_limit', 'card_name', 'key_benefit_1', 'key_benefit_2'],
          guardrails: ['Never ask for OTP or existing card details', 'Always provide opt-out option', 'Never guarantee card approval'],
          handoff_keywords: ['fraud', 'complaint', 'block', 'lost'] },
        { id: 'account_support', name: 'Account Support',   icon: '🏦', desc: 'Balance, statements, card issues',              mode: 'simple',     ...TEMPLATE_EXAMPLES.account_support },
    ],
    ecommerce: [
        { id: 'order_tracking',  name: 'Order Tracking',    icon: '📦', desc: 'Order status, delivery updates, issues',        mode: 'simple',     ...TEMPLATE_EXAMPLES.order_tracking },
        { id: 'return_refund',   name: 'Return & Refund',   icon: '↩️', desc: 'Initiate returns and track refunds',            mode: 'structured',
          objective: 'Help customers initiate product returns, track return pickup status, and get refund updates.',
          guidelines: 'Be understanding and solution-oriented. Follow return policy strictly. Process requests without excessive questioning.',
          faqs: [{ question: 'How long does refund take?', answer: 'Refunds are processed within 5-7 business days to your original payment method.' }],
          variables: ['customer_name', 'order_id', 'product_name', 'return_reason'],
          guardrails: ['Always check return eligibility before processing', 'Never deny return without valid reason', 'Escalate disputes to human'],
          handoff_keywords: ['fraud', 'complaint', 'consumer forum', 'legal'] },
        { id: 'abandoned_cart',  name: 'Abandoned Cart',    icon: '🛒', desc: 'Re-engage and recover abandoned carts',         mode: 'simple',
          objective: 'Re-engage customers who left items in cart. Understand their hesitation and offer assistance or discount to complete purchase.',
          guidelines: 'Be friendly, not pushy. Understand why they left. Maximum one discount offer. Always respect opt-out.',
          faqs: [{ question: 'Is this discount only for me?', answer: 'Yes, this is a special offer just for you, valid for the next 24 hours.' }],
          variables: ['customer_name', 'cart_value', 'product_names', 'discount_code'],
          guardrails: ['Send maximum 2 follow-up messages', 'Never offer discount more than once', 'Always respect opt-out immediately'],
          handoff_keywords: ['complaint', 'fraud', 'not interested'] },
    ],
    healthcare: [
        { id: 'appointment',     name: 'Appointment Booking', icon: '🏥', desc: 'Book, reschedule, cancel appointments',      mode: 'structured', ...TEMPLATE_EXAMPLES.appointment },
        { id: 'medicine_refill', name: 'Medicine Refill',     icon: '💊', desc: 'Prescription reminder and refill',           mode: 'simple',
          objective: 'Remind patients about prescription refill due dates, collect their order, and arrange home delivery or pharmacy pickup.',
          guidelines: 'Only process refill for existing prescriptions. Never suggest medication changes. Always verify prescription before processing.',
          faqs: [{ question: 'Can I change my medicine?', answer: 'I can only process refills for existing prescriptions. Please consult your doctor for any changes.' }],
          variables: ['patient_name', 'medicine_name', 'prescription_date', 'doctor_name'],
          guardrails: ['Never suggest medication changes', 'Always verify prescription before processing', 'Recommend doctor consultation for new symptoms'],
          handoff_keywords: ['emergency', 'side effect', 'complaint', 'doctor urgent'] },
    ],
    realestate: [
        { id: 'lead_qual',       name: 'Lead Qualification', icon: '🏘️', desc: 'Qualify leads and book site visits',          mode: 'structured',
          objective: 'Qualify real estate leads by understanding property preference, budget, location, and purchase timeline. Schedule site visit for interested leads.',
          guidelines: 'Be enthusiastic about properties. Understand requirements precisely. Never overpromise on pricing or availability.',
          faqs: [{ question: 'Is the price negotiable?', answer: 'Our sales team will discuss the best price options when you visit. Shall I schedule a site visit?' }],
          variables: ['customer_name', 'mobile_number', 'budget_range', 'preferred_location', 'property_type'],
          guardrails: ['Never quote final price without checking with sales team', 'Always get proper contact details before booking visit'],
          handoff_keywords: ['legal', 'complaint', 'fraud', 'refund', 'cancel'] },
    ],
    hr: [
        { id: 'candidate',       name: 'Candidate Screening', icon: '👤', desc: 'Initial candidate screening',                mode: 'structured', ...TEMPLATE_EXAMPLES.candidate },
    ],
};

const INDUSTRIES = [
    { id: 'all',        label: 'All Templates' },
    { id: 'banking',    label: 'Banking / NBFC' },
    { id: 'ecommerce',  label: 'E-commerce' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'realestate', label: 'Real Estate' },
    { id: 'hr',         label: 'HR & Recruitment' },
];

const AVATARS   = ['🤖', '👩‍💼', '👨‍💼', '🧑‍💻', '👩‍⚕️', '👨‍⚕️', '🏦', '🛍️', '🏠', '📞', '💬', '⚡'];
const LANGUAGES = [
    { value: 'en',       label: 'English' },
    { value: 'hi',       label: 'Hindi' },
    { value: 'hinglish', label: 'Hinglish (Hindi + English)' },
    { value: 'mr',       label: 'Marathi' },
    { value: 'ta',       label: 'Tamil' },
    { value: 'te',       label: 'Telugu' },
    { value: 'kn',       label: 'Kannada' },
    { value: 'gu',       label: 'Gujarati' },
    { value: 'bn',       label: 'Bengali' },
];
const STEPS = ['Choose Type', 'Identity', 'Brain', 'Tools', 'Knowledge', 'Guardrails', 'Deploy'];

// ── Tooltip component ─────────────────────────────────────────────────────────
function Tooltip({ text }) {
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    return (
        <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
            <i className="bi bi-info-circle"
                style={{ fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}/>
            {show && (
                <div style={{ position: 'absolute', left: '50%', bottom: '130%', transform: 'translateX(-50%)', background: '#1e293b', color: '#f8fafc', padding: '8px 12px', borderRadius: 8, fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-line', width: 220, zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', pointerEvents: 'none' }}>
                    {text}
                    <div style={{ position: 'absolute', left: '50%', top: '100%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #1e293b' }}/>
                </div>
            )}
        </span>
    );
}

// ── Fill with example button ──────────────────────────────────────────────────
function FillExample({ onClick }) {
    return (
        <button onClick={onClick} type="button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#eff6ff', color: '#1565c0', border: '1px solid #bfdbfe', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 8 }}>
            <i className="bi bi-stars" style={{ fontSize: 10 }}></i> Fill with example
        </button>
    );
}

// ── Step bar ──────────────────────────────────────────────────────────────────
function StepBar({ current, steps }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
            {steps.map((step, i) => (
                <React.Fragment key={step}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                            background: i <= current ? '#1565c0' : '#e5e7eb',
                            color: i <= current ? '#fff' : '#9ca3af' }}>
                            {i < current ? <i className="bi bi-check" style={{ fontSize: 14 }}></i> : i + 1}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: i === current ? 700 : 500, color: i === current ? '#1565c0' : i < current ? '#374151' : '#9ca3af', whiteSpace: 'nowrap' }}>
                            {step}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div style={{ flex: 1, height: 2, background: i < current ? '#1565c0' : '#e5e7eb', margin: '0 4px', marginBottom: 22, minWidth: 20 }}/>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ── Field components ──────────────────────────────────────────────────────────
const Label = ({ children, required, tooltip }) => (
    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'inline-flex', alignItems: 'center', marginBottom: 6 }}>
        {children}
        {required && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
        {tooltip && <Tooltip text={tooltip}/>}
    </label>
);
const Hint    = ({ children }) => <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{children}</p>;
const Field   = ({ children, style }) => <div style={{ marginBottom: 20, ...style }}>{children}</div>;
const Input   = (props) => (
    <input {...props} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#111827', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', ...props.style }}
        onFocus={e => e.target.style.borderColor = '#1565c0'}
        onBlur={e => e.target.style.borderColor = '#e5e7eb'}/>
);
const TArea   = (props) => (
    <textarea {...props} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#111827', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, ...props.style }}
        onFocus={e => e.target.style.borderColor = '#1565c0'}
        onBlur={e => e.target.style.borderColor = '#e5e7eb'}/>
);


// ── Step Tools Component ──────────────────────────────────────────────────────
function StepTools({ tools, form, set }) {
    const CONNECTOR_META = {
        custom:          { icon: '🔧', color: '#1565c0', bg: '#eff6ff' },
        google_calendar: { icon: '📅', color: '#16a34a', bg: '#f0fdf4' },
        razorpay:        { icon: '💳', color: '#7c3aed', bg: '#faf5ff' },
        shopify:         { icon: '🛍️', color: '#16a34a', bg: '#f0fdf4' },
        zoho_crm:        { icon: '👥', color: '#d97706', bg: '#fffbeb' },
    };

    const toggleTool = (toolId) => {
        const id  = String(toolId);
        const ids = (form.tool_ids || []).map(String);
        if (ids.includes(id)) {
            set('tool_ids', ids.filter(t => t !== id));
        } else {
            set('tool_ids', [...ids, id]);
        }
    };

    const isSelected = (toolId) => (form.tool_ids || []).map(String).includes(String(toolId));

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>Attach Tools</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                        Select which API tools this agent can call during conversations.
                        Agent will decide when to use each tool based on the conversation context.
                    </p>
                </div>
                <button onClick={() => window.open('/tools/create', '_blank')}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    <i className="bi bi-plus-lg"></i> Create New Tool
                </button>
            </div>

            {tools.length === 0 ? (
                <div style={{ background: '#fff', border: '2px dashed #e5e7eb', borderRadius: 16, padding: 48, textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>No tools created yet</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>
                        Create API tools first, then attach them to agents.
                        Tools allow agents to call external APIs during conversations.
                    </p>
                    <button onClick={() => window.open('/tools/create', '_blank')}
                        style={{ padding: '10px 20px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>Create Your First Tool
                    </button>
                </div>
            ) : (
                <>
                    {/* Selected count */}
                    {(form.tool_ids?.length || 0) > 0 && (
                        <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#1565c0', fontSize: 14 }}></i>
                            <p style={{ margin: 0, fontSize: 12, color: '#1565c0', fontWeight: 600 }}>
                                {form.tool_ids.length} tool{form.tool_ids.length > 1 ? 's' : ''} selected — agent will use these during conversations
                            </p>
                        </div>
                    )}

                    {/* Tool list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {tools.filter(t => t.is_active).map(tool => {
                            const selected  = isSelected(tool._id);
                            const connector = CONNECTOR_META[tool.connector_type] || CONNECTOR_META.custom;
                            return (
                                <div key={tool._id}
                                    onClick={() => toggleTool(tool._id)}
                                    style={{ padding: '14px 16px', border: `2px solid ${selected ? '#1565c0' : '#e5e7eb'}`, borderRadius: 12, cursor: 'pointer', background: selected ? '#eff6ff' : '#fff', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = '#bfdbfe'; }}
                                    onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = '#e5e7eb'; }}>

                                    {/* Checkbox */}
                                    <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${selected ? '#1565c0' : '#d1d5db'}`, background: selected ? '#1565c0' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {selected && <i className="bi bi-check" style={{ fontSize: 12, color: '#fff' }}></i>}
                                    </div>

                                    {/* Icon */}
                                    <div style={{ width: 40, height: 40, borderRadius: 10, background: connector.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                                        {tool.icon || connector.icon}
                                    </div>

                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 3px' }}>{tool.name}</p>
                                        <p style={{ fontSize: 11, color: '#6b7280', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {tool.description}
                                        </p>
                                    </div>

                                    {/* Badges */}
                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: '#f3f4f6', color: '#374151' }}>
                                            {tool.method}
                                        </span>
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: connector.bg, color: connector.color }}>
                                            {connector.icon}
                                        </span>
                                        <span style={{ fontSize: 10, color: '#9ca3af' }}>
                                            {tool.params?.length || 0} params
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Skip hint */}
                    <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 16 }}>
                        Skip this step if your agent doesn't need to call external APIs.
                        Tools can be added later by editing the agent.
                    </p>
                </>
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CreateAgent() {
    const navigate       = useNavigate();
    const [searchParams] = useSearchParams();
    const agentId        = searchParams.get('id');
    const startStep      = searchParams.get('step') === 'deploy' ? 5 : 0;

    const [step,      setStep]      = useState(startStep);
    const [saving,    setSaving]    = useState(false);
    const [loading,   setLoading]   = useState(!!agentId);
    const [appliedTpl,setAppliedTpl]= useState(null); // track which template was applied

    const [form, setForm] = useState({
        name: '', avatar: '🤖', description: '', mode: 'simple',
        objective: '', guidelines: '', conversation_flow: '', faqs: [],
        rag_tags: [], flow_id: '', guardrails: [], handoff_keywords: [],
        variables: [], language: 'en', secondary_language: '',
        welcome_message: 'Hello! How can I help you today?',
        fallback_message: "I don't have information on that. Please rephrase your question.",
        session_timeout: 1440, max_handoff_wait: 30, confidence_threshold: 0.35,
        phone_number_id: '', whatsapp_no: '',
    });

    const [inputs,   setInputs]   = useState({ guardrail: '', keyword: '', variable: '', faqQ: '', faqA: '' });
    const [flows,    setFlows]    = useState([]);
    const [kbTags,   setKbTags]   = useState([]);
    const [tools,    setTools]    = useState([]);
    const [industry, setIndustry] = useState('all');

    const token = localStorage.getItem('token');
    const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // Get current examples — use applied template data from form, or defaults
    const examples = appliedTpl ? {
        objective:        form.objective        || DEFAULT_EXAMPLES.objective,
        guidelines:       form.guidelines       || DEFAULT_EXAMPLES.guidelines,
        faqs:             form.faqs?.length > 0  ? form.faqs  : DEFAULT_EXAMPLES.faqs,
        variables:        form.variables?.length > 0 ? form.variables : DEFAULT_EXAMPLES.variables,
        guardrails:       form.guardrails?.length > 0 ? form.guardrails : DEFAULT_EXAMPLES.guardrails,
        handoff_keywords: form.handoff_keywords?.length > 0 ? form.handoff_keywords : DEFAULT_EXAMPLES.handoff_keywords,
    } : DEFAULT_EXAMPLES;

    useEffect(() => {
        if (agentId) {
            (async () => {
                try {
                    const res  = await fetch(`${url}/agents/${agentId}`, { headers: { Authorization: `Bearer ${token}` } });
                    const data = await res.json();
                    if (data.success) {
                        setForm(p => ({ ...p, ...data.data }));
                        if (data.data.template_id) setAppliedTpl(data.data.template_id);
                    }
                } catch (e) { toast.error('Failed to load agent'); }
                setLoading(false);
            })();
        }
    }, [agentId]);

    useEffect(() => {
        (async () => {
            // Fetch tools from Project B (always available)
            try {
                const toolRes  = await fetch(`${url}/tools`, { headers: { Authorization: `Bearer ${token}` } });
                const toolData = await toolRes.json();
                if (toolData.success) setTools(toolData.data || []);
            } catch (e) { /* ignored */ }

            // Fetch flows and tags from Project C (may be down)
            try {
                const [fRes, tRes] = await Promise.all([
                    fetch(`${chatbotUrl}/flows`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${chatbotUrl}/tags`,  { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                const fData = await fRes.json();
                const tData = await tRes.json();
                if (fData.success) setFlows(fData.data || []);
                if (tData.success) setKbTags(tData.data || []);
            } catch (e) { /* ignored */ }
        })();
    }, []);

    const applyTemplate = (tpl) => {
        setForm(p => ({
            ...p,
            mode:             tpl.mode,
            objective:        tpl.objective   || '',
            guidelines:       tpl.guidelines  || '',
            variables:        tpl.variables   || [],
            guardrails:       tpl.guardrails  || [],
            handoff_keywords: tpl.handoff_keywords || [],
            faqs:             tpl.faqs        || [],
            name:             p.name || tpl.name,
            description:      p.description || tpl.desc,
            template_id:      tpl.id,
        }));
        setAppliedTpl(tpl.id);
        setStep(1);
        toast.success(`Template "${tpl.name}" applied!`, { autoClose: 1500 });
    };

    const addToList = (field, inputKey) => {
        const val = inputs[inputKey]?.trim();
        if (!val) return;
        if (!form[field].includes(val)) set(field, [...form[field], val]);
        setInputs(p => ({ ...p, [inputKey]: '' }));
    };

    const removeFromList = (field, idx) => set(field, form[field].filter((_, i) => i !== idx));

    const addFaq = () => {
        if (!inputs.faqQ.trim() || !inputs.faqA.trim()) return;
        set('faqs', [...form.faqs, { question: inputs.faqQ.trim(), answer: inputs.faqA.trim() }]);
        setInputs(p => ({ ...p, faqQ: '', faqA: '' }));
    };

    const fillExamples = () => {
        setForm(p => ({
            ...p,
            objective:        p.objective  || examples.objective,
            guidelines:       p.guidelines || examples.guidelines,
            faqs:             p.faqs.length > 0 ? p.faqs : examples.faqs,
            variables:        p.variables.length > 0 ? p.variables : examples.variables,
            guardrails:       p.guardrails.length > 0 ? p.guardrails : examples.guardrails,
            handoff_keywords: p.handoff_keywords.length > 0 ? p.handoff_keywords : examples.handoff_keywords,
        }));
        toast.success('Examples filled! Edit them to match your use case.', { autoClose: 2000 });
    };

    const saveAgent = async (publish = false) => {
        if (!form.name.trim()) { toast.error('Agent name is required'); return; }
        setSaving(true);
        try {
            const method   = agentId ? 'PATCH' : 'POST';
            const endpoint = agentId ? `${url}/agents/${agentId}` : `${url}/agents`;
            const res      = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!data.success) { toast.error(data.error); setSaving(false); return; }
            const savedId = data.data._id;

            if (publish && form.phone_number_id) {
                const pRes  = await fetch(`${url}/agents/${savedId}/publish`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ phone_number_id: form.phone_number_id, whatsapp_no: form.whatsapp_no }),
                });
                const pData = await pRes.json();
                if (pData.success) {
                    toast.success('Agent is now live! 🚀', { autoClose: 3000 });
                    setTimeout(() => navigate('/agents'), 1500);
                } else {
                    toast.error(pData.error);
                }
            } else {
                toast.success(agentId ? 'Agent updated' : 'Agent saved as draft', { autoClose: 2000 });
                if (!publish) setTimeout(() => navigate('/agents'), 1500);
            }
        } catch (e) { toast.error('Save failed'); }
        setSaving(false);
    };

    const allTemplates     = Object.values(TEMPLATES).flat();
    const visibleTemplates = industry === 'all' ? allTemplates : (TEMPLATES[industry] || []);

    if (loading) return (
        <WrapperComponent>
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
                <div className="spinner-border text-primary"></div>
            </div>
        </WrapperComponent>
    );

    return (
        <WrapperComponent>
            <div style={{ maxWidth: 820, margin: '0 auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                    <button onClick={() => navigate('/agents')}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#374151' }}>
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>
                            {agentId ? 'Edit Agent' : 'Create Agent'}
                        </h5>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
                            {agentId ? `Editing: ${form.name}` : 'Build your WhatsApp AI agent step by step'}
                        </p>
                    </div>
                    {/* Fill all examples button — shown on Brain and Guardrails steps */}
                    {(step === 2 || step === 5) && (
                        <button onClick={fillExamples}
                            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                            <i className="bi bi-stars"></i>
                            {appliedTpl ? 'Fill template examples' : 'Fill with examples'}
                        </button>
                    )}
                </div>

                <StepBar current={step} steps={STEPS}/>

                {/* ── STEP 0: Choose Type ─────────────────────────────────── */}
                {step === 0 && (
                    <Step0
                        form={form} set={set}
                        setStep={setStep} applyTemplate={applyTemplate}
                        url={url} token={token}
                    />
                )}

                {/* ── STEP 1: Identity ────────────────────────────────────── */}
                {step === 1 && (
                    <div>
                        <Field>
                            <Label tooltip="Choose an emoji that represents your agent's personality or industry.">Avatar</Label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {AVATARS.map(a => (
                                    <button key={a} onClick={() => set('avatar', a)}
                                        style={{ width: 44, height: 44, fontSize: 22, borderRadius: 10, border: `2px solid ${form.avatar === a ? '#1565c0' : '#e5e7eb'}`, background: form.avatar === a ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
                                        {a}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field>
                            <Label required tooltip="Give your agent a clear name that reflects its purpose. E.g. 'Customer Support Agent', 'Loan Advisor'">Agent Name</Label>
                            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Customer Support Agent, EMI Reminder Bot, Sales Assistant"/>
                        </Field>
                        <Field>
                            <Label tooltip="A brief one-line description of what this agent does. Shown on the agents list for easy identification.">Description</Label>
                            <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Handles account queries and card issues for retail banking customers"/>
                        </Field>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field>
                                <Label required tooltip="The main language your agent will communicate in. The agent will automatically detect and respond in this language.">Primary Language</Label>
                                <select value={form.language} onChange={e => set('language', e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}>
                                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                            </Field>
                            <Field>
                                <Label tooltip="If your customers use a second language, add it here. The agent will handle both languages seamlessly.">Secondary Language</Label>
                                <select value={form.secondary_language} onChange={e => set('secondary_language', e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}>
                                    <option value="">None</option>
                                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                            </Field>
                        </div>
                        <Field>
                            <Label tooltip="The first message your agent sends when a customer says hello or starts a new conversation.">Welcome Message</Label>
                            <Input value={form.welcome_message} onChange={e => set('welcome_message', e.target.value)} placeholder="e.g. Hello! Welcome to XYZ Bank support. How can I help you today?"/>
                        </Field>
                        <Field>
                            <Label tooltip="Shown when the agent cannot find an answer in the knowledge base. Should guide the customer on what to do next.">Fallback Message</Label>
                            <Input value={form.fallback_message} onChange={e => set('fallback_message', e.target.value)} placeholder="e.g. I don't have information on that. Please rephrase or type 'agent' to speak with a human."/>
                        </Field>
                    </div>
                )}

                {/* ── STEP 2: Brain ───────────────────────────────────────── */}
                {step === 2 && (
                    <div>
                        {/* Template applied banner */}
                        {appliedTpl && (
                            <div style={{ padding: '10px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bi bi-check-circle-fill" style={{ color: '#16a34a', fontSize: 14 }}></i>
                                <p style={{ margin: 0, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                                    Template applied — fields below are pre-filled. Edit them to match your specific use case.
                                </p>
                            </div>
                        )}

                        {/* Mode selector */}
                        <Field>
                            <Label required tooltip="How your agent handles conversations.\n\nSimple Agent: Give it a goal, AI figures out the conversation.\n\nStructured Agent: Define stages, AI drives within each stage.\n\nFlow Mode: You design every step with the visual flow builder.">
                                Agent Mode
                            </Label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                {[
                                    { mode: 'simple',     icon: 'bi-lightning-charge', label: 'Simple Agent'     },
                                    { mode: 'structured', icon: 'bi-layers',           label: 'Structured Agent' },
                                    { mode: 'flow',       icon: 'bi-diagram-3',        label: 'Flow Mode'        },
                                ].map(m => (
                                    <button key={m.mode} onClick={() => set('mode', m.mode)}
                                        style={{ padding: '12px 8px', border: `2px solid ${form.mode === m.mode ? '#1565c0' : '#e5e7eb'}`, borderRadius: 10, background: form.mode === m.mode ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: form.mode === m.mode ? '#1565c0' : '#374151', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                        <i className={`bi ${m.icon}`} style={{ fontSize: 18 }}></i>
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {(form.mode === 'simple' || form.mode === 'structured') && (
                            <>
                                {/* Objective */}
                                <Field>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Label required tooltip="Tell the agent what its main job is in plain English.\n\nBe specific:\n• What should it help customers with?\n• What should it NOT do?\n• When should it escalate to a human?\n\nThe more specific you are, the better the agent performs.">
                                            Objective
                                        </Label>
                                        {!form.objective && <FillExample onClick={() => set('objective', examples.objective)}/>}
                                    </div>
                                    <TArea value={form.objective} onChange={e => set('objective', e.target.value)}
                                        rows={4} placeholder={`e.g. ${examples.objective}`}/>
                                    <Hint>Be specific about what the agent should and should not do. The clearer the objective, the better the agent performs.</Hint>
                                </Field>

                                {/* Guidelines */}
                                <Field>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Label tooltip="Define how the agent should communicate.\n\nExamples:\n• Tone (formal, friendly, empathetic)\n• Language style\n• Things it must always do\n• Things it must never do\n• How to handle difficult customers">
                                            Response Guidelines
                                        </Label>
                                        {!form.guidelines && <FillExample onClick={() => set('guidelines', examples.guidelines)}/>}
                                    </div>
                                    <TArea value={form.guidelines} onChange={e => set('guidelines', e.target.value)}
                                        rows={4} placeholder={`e.g. ${examples.guidelines}`}/>
                                    <Hint>Define the agent's tone, style, and behavior. Think of this as training instructions for a new employee.</Hint>
                                </Field>

                                {/* Structured agent conversation flow */}
                                {form.mode === 'structured' && (
                                    <Field>
                                        <Label tooltip="Break the conversation into stages.\n\nExample:\nStage 1 - Greeting: Welcome customer, identify their query.\nStage 2 - Resolution: Search knowledge base, attempt to resolve.\nStage 3 - Closure: Confirm resolution or escalate to human.\n\nThe AI will handle each stage intelligently.">
                                            Conversation Stages
                                        </Label>
                                        <TArea value={form.conversation_flow} onChange={e => set('conversation_flow', e.target.value)}
                                            rows={5} placeholder="Stage 1 - Greeting: Welcome customer and identify their issue type.&#10;Stage 2 - Resolution: Search knowledge base and attempt resolution.&#10;Stage 3 - Closure: Confirm if resolved or escalate to human agent."/>
                                        <Hint>Describe each stage and what should happen. AI will drive conversations within each stage automatically.</Hint>
                                    </Field>
                                )}

                                {/* FAQs */}
                                <Field>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Label tooltip="Add common questions and their answers directly here.\n\nThe agent will use these to answer instantly without searching the knowledge base.\n\nPerfect for:\n• Working hours\n• Contact details\n• Simple policy questions\n• Common objections">
                                            FAQs (Inline Q&A)
                                        </Label>
                                        {form.faqs.length === 0 && (
                                            <FillExample onClick={() => set('faqs', examples.faqs)}/>
                                        )}
                                    </div>
                                    <Hint>Add questions customers frequently ask. Agent will answer these instantly without searching the knowledge base.</Hint>
                                    <div style={{ marginTop: 10 }}>
                                        {form.faqs.map((faq, i) => (
                                            <div key={i} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                                <div style={{ flex: 1 }}>
                                                    <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 2px' }}>Q: {faq.question}</p>
                                                    <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>A: {faq.answer}</p>
                                                </div>
                                                <button onClick={() => removeFromList('faqs', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px 6px' }}>
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginTop: 8 }}>
                                            <Input value={inputs.faqQ} onChange={e => setInputs(p => ({ ...p, faqQ: e.target.value }))} placeholder="Question"/>
                                            <Input value={inputs.faqA} onChange={e => setInputs(p => ({ ...p, faqA: e.target.value }))} placeholder="Answer" onKeyDown={e => e.key === 'Enter' && addFaq()}/>
                                            <button onClick={addFaq}
                                                style={{ padding: '10px 14px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </Field>

                                {/* Variables */}
                                <Field>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Label tooltip="Variables are pieces of information the agent will collect during the conversation.\n\nExamples:\n• customer_name → agent asks for customer's name\n• order_id → agent asks for order number\n• account_number → agent asks for account number\n\nUse snake_case (lowercase with underscores).">
                                            Custom Variables
                                        </Label>
                                        {form.variables.length === 0 && (
                                            <FillExample onClick={() => set('variables', examples.variables)}/>
                                        )}
                                    </div>
                                    <Hint>Information the agent will collect from customers during the conversation (e.g. customer_name, order_id, account_number)</Hint>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                        {form.variables.map((v, i) => (
                                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: '#f3f4f6', borderRadius: 20, fontSize: 12, fontFamily: 'monospace', color: '#374151', border: '1px solid #e5e7eb' }}>
                                                {'{{'}{v}{'}}'}
                                                <button onClick={() => removeFromList('variables', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, lineHeight: 1 }}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                        <Input value={inputs.variable} onChange={e => setInputs(p => ({ ...p, variable: e.target.value }))}
                                            placeholder="e.g. customer_name, order_id, account_number"
                                            onKeyDown={e => e.key === 'Enter' && addToList('variables', 'variable')} style={{ flex: 1 }}/>
                                        <button onClick={() => addToList('variables', 'variable')}
                                            style={{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                            Add
                                        </button>
                                    </div>
                                </Field>
                            </>
                        )}

                        {/* Flow mode */}
                        {form.mode === 'flow' && (
                            <Field>
                                <Label required tooltip="Select the conversation flow you built in the Flow Builder.\n\nThe agent will follow the exact sequence of nodes you designed.\n\nIf you haven't built a flow yet, go to Flow Builder first.">
                                    Select Flow
                                </Label>
                                {flows.length === 0 ? (
                                    <div style={{ padding: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, textAlign: 'center' }}>
                                        <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>No flows found. <a href="/flows" style={{ color: '#1565c0' }}>Create a flow first</a> then come back.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {flows.map(f => {
                                            const fId = String(f._id || f.id || '');
                                            return (
                                                <div key={fId} onClick={() => set('flow_id', fId)}
                                                    style={{ padding: '12px 16px', border: `2px solid ${form.flow_id === fId ? '#1565c0' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: form.flow_id === fId ? '#eff6ff' : '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <i className="bi bi-diagram-3" style={{ color: form.flow_id === fId ? '#1565c0' : '#6b7280' }}></i>
                                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827', flex: 1 }}>{f.flow_name || f.name}</p>
                                                    {form.flow_id === fId && <i className="bi bi-check-circle-fill" style={{ color: '#1565c0' }}></i>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Field>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Tools ────────────────────────────────────────── */}
                {step === 3 && (
                    <StepTools tools={tools} form={form} set={set}/>
                )}

                {/* ── STEP 4: Knowledge ───────────────────────────────────── */}
                {step === 4 && (
                    <div>
                        <Field>
                            <Label tooltip="Select which knowledge base categories this agent can search.\n\nWhen a customer asks a question, the agent searches these categories to find the answer.\n\nTip: Upload documents in the Knowledge Base section first, then come back here to select the relevant tags.">
                                Knowledge Base Tags
                            </Label>
                            <Hint>Select which knowledge base categories this agent can search to answer customer questions</Hint>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                                {kbTags.length > 0 ? kbTags.map(tag => {
                                    const tagName = tag.name || tag.tag_name || '';
                                    const selected = form.rag_tags.includes(tagName);
                                    return (
                                        <button key={tag._id} onClick={() => selected ? set('rag_tags', form.rag_tags.filter(t => t !== tagName)) : set('rag_tags', [...form.rag_tags, tagName])}
                                            style={{ padding: '8px 16px', border: `2px solid ${selected ? '#1565c0' : '#e5e7eb'}`, borderRadius: 20, background: selected ? '#eff6ff' : '#fff', color: selected ? '#1565c0' : '#374151', cursor: 'pointer', fontSize: 13, fontWeight: selected ? 700 : 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {selected && <i className="bi bi-check" style={{ fontSize: 12 }}></i>}
                                            {tagName}
                                        </button>
                                    );
                                }) : (
                                    <div style={{ padding: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, width: '100%', textAlign: 'center' }}>
                                        <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>No knowledge base tags found. <a href="/knowledgebase" style={{ color: '#1565c0' }}>Upload documents first</a>.</p>
                                    </div>
                                )}
                            </div>
                            {form.rag_tags.length > 0 && (
                                <p style={{ fontSize: 12, color: '#16a34a', marginTop: 10, fontWeight: 600 }}>
                                    <i className="bi bi-check-circle" style={{ marginRight: 6 }}></i>
                                    {form.rag_tags.length} tag(s) selected: {form.rag_tags.join(', ')}
                                </p>
                            )}
                        </Field>

                        <Field>
                            <Label tooltip="How confident the agent needs to be before using a knowledge base answer.\n\nHigher value (e.g. 0.8) = Very strict — agent only answers when very sure. More fallbacks.\n\nLower value (e.g. 0.3) = Lenient — agent answers even when moderately sure. Fewer fallbacks.\n\nRecommended: 0.35 for most use cases.">
                                Confidence Threshold
                            </Label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                                <input type="range" min="0.1" max="0.9" step="0.05"
                                    value={form.confidence_threshold}
                                    onChange={e => set('confidence_threshold', parseFloat(e.target.value))}
                                    style={{ flex: 1 }}/>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#1565c0', minWidth: 40 }}>
                                    {form.confidence_threshold}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                                <span>Lenient — more answers</span>
                                <span>Strict — fewer answers</span>
                            </div>
                            <Hint>Recommended: 0.35. Increase if agent is giving irrelevant answers. Decrease if agent gives too many fallbacks.</Hint>
                        </Field>
                    </div>
                )}

                {/* ── STEP 5: Guardrails ──────────────────────────────────── */}
                {step === 5 && (
                    <div>
                        {/* Fill examples button context */}
                        {appliedTpl && (
                            <div style={{ padding: '10px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bi bi-info-circle" style={{ color: '#16a34a', fontSize: 14 }}></i>
                                <p style={{ margin: 0, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                                    Template guardrails and keywords are pre-filled. Review and customize for your business.
                                </p>
                            </div>
                        )}

                        {/* Guardrails */}
                        <Field>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <Label tooltip="Rules the agent must ALWAYS follow, regardless of what the customer says.\n\nExamples:\n• Never share full account numbers\n• Always verify identity before sharing details\n• Escalate fraud cases immediately\n• Never promise loan approval\n\nThese are non-negotiable — the agent will not break them even if the customer insists.">
                                    Guardrails
                                </Label>
                                {form.guardrails.length === 0 && (
                                    <FillExample onClick={() => set('guardrails', examples.guardrails)}/>
                                )}
                            </div>
                            <Hint>Hard rules the agent must always follow. Think of these as compliance boundaries that cannot be crossed.</Hint>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                                {form.guardrails.map((g, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                                        <i className="bi bi-shield-check" style={{ color: '#1565c0', fontSize: 13, flexShrink: 0 }}></i>
                                        <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{g}</span>
                                        <button onClick={() => removeFromList('guardrails', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px 6px' }}>
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                <Input value={inputs.guardrail} onChange={e => setInputs(p => ({ ...p, guardrail: e.target.value }))}
                                    placeholder="e.g. Never share account details without verifying customer identity first"
                                    onKeyDown={e => e.key === 'Enter' && addToList('guardrails', 'guardrail')} style={{ flex: 1 }}/>
                                <button onClick={() => addToList('guardrails', 'guardrail')}
                                    style={{ padding: '10px 16px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                    Add
                                </button>
                            </div>
                        </Field>

                        {/* Handoff keywords */}
                        <Field>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <Label tooltip="Words or phrases that immediately trigger transfer to a human agent.\n\nPlatform already handles common ones like 'agent', 'human', 'help', 'complaint'.\n\nAdd your business-specific keywords here.\n\nExamples for banking:\n• fraud, unauthorized, stolen\n• legal, court, rbi, ombudsman\n• cancel, close account\n\nExamples for e-commerce:\n• consumer forum, legal notice\n• fraud, not delivered">
                                    Custom Handoff Keywords
                                </Label>
                                {form.handoff_keywords.length === 0 && (
                                    <FillExample onClick={() => set('handoff_keywords', examples.handoff_keywords)}/>
                                )}
                            </div>
                            <Hint>Words that trigger immediate transfer to human agent. Platform already handles "agent", "human", "complaint" etc. Add your domain-specific ones here.</Hint>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                {form.handoff_keywords.map((k, i) => (
                                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 20, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                                        {k}
                                        <button onClick={() => removeFromList('handoff_keywords', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 0 }}>×</button>
                                    </span>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <Input value={inputs.keyword} onChange={e => setInputs(p => ({ ...p, keyword: e.target.value }))}
                                    placeholder="e.g. fraud, legal, rbi, ombudsman, cancel"
                                    onKeyDown={e => e.key === 'Enter' && addToList('handoff_keywords', 'keyword')} style={{ flex: 1 }}/>
                                <button onClick={() => addToList('handoff_keywords', 'keyword')}
                                    style={{ padding: '10px 16px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                    Add
                                </button>
                            </div>
                        </Field>

                        {/* Timeout settings */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field>
                                <Label tooltip="How long a WhatsApp session stays active after the last message.\n\n1440 minutes = 24 hours (Meta's standard).\n\nAfter this time, if the customer messages again, a new session starts with the welcome message.">
                                    Session Timeout (minutes)
                                </Label>
                                <Input type="number" value={form.session_timeout} onChange={e => set('session_timeout', parseInt(e.target.value))} min={5} max={1440}/>
                                <Hint>1440 = 24 hours (Meta standard). Reduce for shorter sessions.</Hint>
                            </Field>
                            <Field>
                                <Label tooltip="If a customer is waiting in the agent queue, how long before we notify them?\n\nAfter this time, the customer gets a message:\n'We are sorry for the wait. Our team will respond within 2 hours.'\n\nRecommended: 30 minutes.">
                                    Queue Wait Timeout (minutes)
                                </Label>
                                <Input type="number" value={form.max_handoff_wait} onChange={e => set('max_handoff_wait', parseInt(e.target.value))} min={5} max={120}/>
                                <Hint>Notify customer if no agent available after this time.</Hint>
                            </Field>
                        </div>
                    </div>
                )}

                {/* ── STEP 6: Deploy ──────────────────────────────────────── */}
                {step === 6 && (
                    <div>
                        {/* Summary card */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 24 }}>
                            <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 26 }}>{form.avatar}</span> {form.name || 'Unnamed Agent'}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {[
                                    { label: 'Mode',       value: form.mode === 'simple' ? 'Simple Agent' : form.mode === 'structured' ? 'Structured Agent' : 'Flow Mode' },
                                    { label: 'Language',   value: LANGUAGES.find(l => l.value === form.language)?.label || form.language },
                                    { label: 'KB Tags',    value: form.rag_tags.length > 0 ? form.rag_tags.join(', ') : 'None selected' },
                                    { label: 'Tools',      value: `${form.tool_ids?.length || 0} tools attached` },
                                    { label: 'Guardrails', value: `${form.guardrails.length} rules defined` },
                                    { label: 'Variables',  value: form.variables.length > 0 ? form.variables.join(', ') : 'None' },
                                    { label: 'FAQs',       value: `${form.faqs.length} inline answers` },
                                ].map(item => (
                                    <div key={item.label}>
                                        <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', margin: '0 0 3px', letterSpacing: '0.06em' }}>{item.label}</p>
                                        <p style={{ fontSize: 13, color: '#374151', margin: 0, fontWeight: 500 }}>{item.value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Field>
                            <Label tooltip="Enter your Meta WhatsApp Business phone number ID to deploy this agent.\n\nWhere to find it:\n1. Go to Meta Business Manager\n2. WhatsApp → Phone Numbers\n3. Copy the Phone Number ID (not the phone number itself)\n\nLeave empty to save as draft and deploy later.">
                                Attach to WhatsApp Number
                            </Label>
                            <Hint>Leave empty to save as draft. Fill in to deploy and go live immediately.</Hint>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                                <div>
                                    <Label tooltip="The numeric ID of your WhatsApp Business phone number from Meta Business Manager. Looks like: 518623944663807">Phone Number ID</Label>
                                    <Input value={form.phone_number_id} onChange={e => set('phone_number_id', e.target.value)} placeholder="e.g. 518623944663807"/>
                                </div>
                                <div>
                                    <Label tooltip="Your WhatsApp number in international format. Used for display purposes only.">WhatsApp Number</Label>
                                    <Input value={form.whatsapp_no} onChange={e => set('whatsapp_no', e.target.value)} placeholder="e.g. +91 98765 43210"/>
                                </div>
                            </div>
                        </Field>

                        {form.phone_number_id && (
                            <div style={{ padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, marginBottom: 16 }}>
                                <p style={{ margin: 0, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
                                    <i className="bi bi-check-circle" style={{ marginRight: 8 }}></i>
                                    Ready to go live! Click "Save & Go Live" to publish this agent on {form.whatsapp_no || form.phone_number_id}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Navigation ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 32, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
                    <button onClick={() => step === 0 ? navigate('/agents') : setStep(p => p - 1)}
                        style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="bi bi-arrow-left"></i> {step === 0 ? 'Cancel' : 'Back'}
                    </button>

                    <div style={{ display: 'flex', gap: 10 }}>
                        {step > 0 && (
                            <button onClick={() => saveAgent(false)} disabled={saving}
                                style={{ padding: '10px 20px', background: '#fff', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                Save as Draft
                            </button>
                        )}
                        {step < STEPS.length - 1 ? (
                            <button onClick={() => setStep(p => p + 1)}
                                style={{ padding: '10px 24px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                Next <i className="bi bi-arrow-right"></i>
                            </button>
                        ) : (
                            <>
                                <button onClick={() => saveAgent(false)} disabled={saving}
                                    style={{ padding: '10px 20px', background: '#fff', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className="bi bi-floppy"></i> Save as Draft
                                </button>
                                <button onClick={() => saveAgent(true)} disabled={saving || !form.phone_number_id}
                                    style={{ padding: '10px 24px', background: form.phone_number_id ? 'linear-gradient(135deg, #16a34a, #15803d)' : '#d1d5db', color: '#fff', border: 'none', borderRadius: 10, cursor: saving || !form.phone_number_id ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
                                    {saving
                                        ? <><div className="spinner-border spinner-border-sm text-light" style={{ width: '1rem', height: '1rem' }}></div> Saving...</>
                                        : <><i className="bi bi-rocket"></i> Save & Go Live</>}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <ToastContainer/>
        </WrapperComponent>
    );
}
